import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const HowItWorks = () => {
  useEffect(() => {
    // Set page title
    document.title = "How to Sell Industrial Equipment - 4 Step Process Texas Oklahoma Louisiana | ValveSalesSupply";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Learn our simple 4-step process to sell surplus industrial equipment. From contact to payment in 72 hours. Professional service, above scrap rates, complete documentation. Serving refineries across Texas, Oklahoma, Louisiana, and 6 more states.');
    }
    
    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'how to sell industrial equipment, sell surplus valves process, industrial equipment selling process, refinery equipment disposal, equipment removal service, turnaround equipment buyers');
    }

    // Add canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://valvesalessupply.com/how-it-works');

    // Add Open Graph tags
    const ogTags = [
      { property: 'og:title', content: 'How to Sell Industrial Equipment - Simple 4-Step Process | ValveSalesSupply' },
      { property: 'og:description', content: 'Simple 4-step process to sell your surplus industrial equipment. From contact to payment, we handle everything professionally.' },
      { property: 'og:url', content: 'https://valvesalessupply.com/how-it-works' },
      { property: 'og:type', content: 'website' }
    ];

    ogTags.forEach(tag => {
      let meta = document.querySelector(`meta[property="${tag.property}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', tag.property);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', tag.content);
    });

    // Add Schema.org structured data
    const schemaData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://valvesalessupply.com/how-it-works/#webpage",
          "url": "https://valvesalessupply.com/how-it-works",
          "name": "How to Sell Industrial Equipment - ValveSalesSupply Process",
          "description": "Learn our simple 4-step process to sell your surplus industrial equipment. From contact to payment, we make selling your valves and instrumentation easy.",
          "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://valvesalessupply.com"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "How It Works",
                "item": "https://valvesalessupply.com/how-it-works"
              }
            ]
          }
        },
        {
          "@type": "HowTo",
          "name": "How to Sell Your Surplus Industrial Equipment",
          "description": "Complete guide to selling your surplus valves, instrumentation, and process controls to ValveSalesSupply",
          "totalTime": "PT72H",
          "estimatedCost": {
            "@type": "MonetaryAmount",
            "currency": "USD",
            "value": "0"
          },
          "supply": [
            {
              "@type": "HowToSupply",
              "name": "Equipment List"
            },
            {
              "@type": "HowToSupply", 
              "name": "Equipment Photos"
            },
            {
              "@type": "HowToSupply",
              "name": "Asset Tags or Serial Numbers"
            }
          ],
          "step": [
            {
              "@type": "HowToStep",
              "position": 1,
              "name": "Contact Us",
              "text": "Call us at 214-854-5638 or submit your equipment list online. We respond within 24 hours with initial pricing.",
              "url": "https://valvesalessupply.com/contact",
              "image": "https://public.readdy.ai/ai/img_res/ac712876db36432010f81fc55e90ffa0.jpg"
            },
            {
              "@type": "HowToStep",
              "position": 2,
              "name": "Equipment Assessment",
              "text": "Our experts review your inventory and provide detailed quotes based on current market rates and equipment condition.",
              "image": "https://readdy.ai/api/search-image?query=industrial%20equipment%20specialist%20examining%20valves%20and%20gauges%20with%20clipboard%20professional%20assessment%20clean%20industrial%20environment&width=400&height=300&seq=assessment-step&orientation=landscape"
            },
            {
              "@type": "HowToStep",
              "position": 3,
              "name": "Schedule Pickup",
              "text": "We coordinate with your turnaround schedule and arrange professional removal services at your convenience.",
              "image": "https://readdy.ai/api/search-image?query=industrial%20truck%20loading%20equipment%20at%20refinery%20professional%20logistics%20industrial%20facility%20background&width=400&height=300&seq=pickup-step&orientation=landscape"
            },
            {
              "@type": "HowToStep",
              "position": 4,
              "name": "Get Paid",
              "text": "Receive payment upon pickup with complete documentation and certificates of destruction if required.",
              "image": "https://readdy.ai/api/search-image?query=business%20handshake%20with%20payment%20documents%20industrial%20office%20setting%20professional%20transaction&width=400&height=300&seq=payment-step&orientation=landscape"
            }
          ]
        },
        {
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "How quickly can you provide a quote?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "We respond within 24 hours with initial pricing and can provide detailed quotes within 48 hours of equipment assessment."
              }
            },
            {
              "@type": "Question",
              "name": "Do you handle equipment removal?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes, we coordinate all aspects of equipment removal including professional crews, proper documentation, and scheduling around your operations."
              }
            },
            {
              "@type": "Question",
              "name": "What documentation do you provide?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "We provide complete transaction documentation including certificates of destruction, asset removal records, and payment receipts for your records."
              }
            },
            {
              "@type": "Question",
              "name": "Can you work around our turnaround schedule?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Absolutely. We understand the importance of coordinating with your maintenance schedules and can arrange pickup during planned turnarounds or shutdowns."
              }
            },
            {
              "@type": "Question",
              "name": "What makes your pricing competitive?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Our extensive network of buyers, refurbishers, and end users allows us to maximize equipment value and consistently pay above scrap market rates."
              }
            }
          ]
        }
      ]
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schemaData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const processSteps = [
    {
      number: '1',
      title: 'Initial Contact',
      subtitle: 'Reach Out to Our Team',
      description: 'Contact us by phone, email, or through our online form. Provide basic information about your equipment and facility location. We respond within 24 hours with initial guidance.',
      icon: 'ri-phone-line',
      details: [
        'Call our direct line for immediate response',
        'Submit equipment list via secure online form',
        'Email detailed inventory with photos',
        'Schedule on-site consultation if needed'
      ],
      timeline: '0-24 Hours'
    },
    {
      number: '2',
      title: 'Equipment Assessment',
      subtitle: 'Professional Evaluation',
      description: 'Our experienced team reviews your equipment inventory, assesses condition, and determines current market value. We provide detailed quotes based on real-time market conditions.',
      icon: 'ri-file-list-3-line',
      details: [
        'Detailed equipment specification review',
        'Current market value assessment',
        'Condition evaluation (on-site if required)',
        'Comprehensive written quote provided'
      ],
      timeline: '1-3 Business Days'
    },
    {
      number: '3',
      title: 'Agreement & Scheduling',
      subtitle: 'Coordinate with Your Timeline',
      description: 'Once terms are agreed upon, we coordinate pickup scheduling with your turnaround timeline. We work around your operational needs and provide all necessary documentation.',
      icon: 'ri-calendar-check-line',
      details: [
        'Flexible scheduling around turnarounds',
        'Coordination with plant maintenance teams',
        'All permits and safety documentation handled',
        'Insurance certificates provided'
      ],
      timeline: '1-2 Weeks Lead Time'
    },
    {
      number: '4',
      title: 'Pickup & Payment',
      subtitle: 'Professional Removal Service',
      description: 'Our certified crews safely remove equipment using proper procedures. Payment is processed immediately upon pickup completion with full documentation provided.',
      icon: 'ri-truck-line',
      details: [
        'Certified rigging and removal crews',
        'All safety protocols followed',
        'Immediate payment upon completion',
        'Certificate of destruction if required'
      ],
      timeline: 'Same Day Payment'
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 sm:pt-24 pb-12 sm:pb-16">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://readdy.ai/api/search-image?query=industrial%20facility%20turnaround%20maintenance%20workers%20removing%20equipment%20professional%20industrial%20scene%20organized%20workflow%20clean%20modern%20industrial%20setting&width=1920&height=600&seq=turnaround-process-001&orientation=landscape')`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#1B3A5F]/80 to-[#1B3A5F]/60"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 text-center text-white">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6">HOW IT WORKS</h1>
          <p className="text-base sm:text-lg lg:text-xl text-gray-200 max-w-3xl mx-auto">
            Our streamlined 4-step process makes selling your surplus equipment simple and profitable. 
            From initial contact to final payment, we handle everything professionally.
          </p>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          {processSteps.map((step, index) => (
            <div key={index} className="mb-12 sm:mb-16 lg:mb-20 last:mb-0">
              <div className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-6 sm:gap-8 lg:gap-12`}>
                {/* Content */}
                <div className="flex-1 w-full">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center mb-4 sm:mb-6">
                    <div className="w-12 sm:w-16 lg:w-20 h-12 sm:h-16 lg:h-20 bg-[#1B3A5F] rounded-full flex items-center justify-center mb-3 sm:mb-0 sm:mr-6">
                      <span className="text-white text-lg sm:text-2xl lg:text-3xl font-bold">{step.number}</span>
                    </div>
                    <div>
                      <h2 className="text-[#1B3A5F] text-xl sm:text-2xl lg:text-3xl font-bold">{step.title}</h2>
                      <p className="text-[#6B7280] text-sm sm:text-base lg:text-lg">{step.subtitle}</p>
                    </div>
                  </div>
                  
                  <p className="text-gray-600 text-sm sm:text-base lg:text-lg mb-4 sm:mb-6 lg:mb-8 leading-relaxed">
                    {step.description}
                  </p>
                  
                  <div className="bg-[#F5F7FA] rounded-xl sm:rounded-2xl p-4 sm:p-6 mb-4 sm:mb-6">
                    <h4 className="text-[#1B3A5F] font-semibold mb-3 sm:mb-4 text-sm sm:text-base">What We Handle:</h4>
                    <div className="grid sm:grid-cols-2 gap-2 sm:gap-3">
                      {step.details.map((detail, detailIndex) => (
                        <div key={detailIndex} className="flex items-start">
                          <i className="ri-check-line text-[#1B3A5F] mr-2 sm:mr-3 text-sm sm:text-base lg:text-lg mt-0.5 flex-shrink-0"></i>
                          <span className="text-gray-700 text-xs sm:text-sm lg:text-base">{detail}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="flex items-center text-[#1B3A5F]">
                    <i className="ri-time-line mr-2 text-sm sm:text-base"></i>
                    <span className="font-semibold text-xs sm:text-sm lg:text-base">Timeline: {step.timeline}</span>
                  </div>
                </div>
                
                {/* Visual */}
                <div className="flex-1 w-full">
                  <div className="bg-[#F5F7FA] rounded-xl sm:rounded-2xl lg:rounded-3xl p-6 sm:p-8 lg:p-12 text-center">
                    <div className="w-16 sm:w-24 lg:w-32 h-16 sm:h-24 lg:h-32 bg-[#1B3A5F] rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 lg:mb-6">
                      <i className={`${step.icon} text-white text-2xl sm:text-3xl lg:text-5xl`}></i>
                    </div>
                    <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-[#1B3A5F] mb-2">{step.title}</h3>
                    <p className="text-gray-600 text-sm sm:text-base">{step.subtitle}</p>
                  </div>
                </div>
              </div>
              
              {/* Progress Line */}
              {index < processSteps.length - 1 && (
                <div className="flex justify-center mt-6 sm:mt-8 lg:mt-12">
                  <div className="w-1 h-8 sm:h-12 lg:h-16 bg-[#6B7280] rounded-full"></div>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* What Makes Us Different */}
      <section className="py-12 sm:py-16 lg:py-20 bg-[#F5F7FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12 lg:mb-16">
            <h2 className="text-[#1B3A5F] text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4">WHY CHOOSE US</h2>
            <p className="text-gray-600 text-base sm:text-lg lg:text-xl">Our professional approach sets us apart from typical scrap buyers</p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {[
              {
                icon: 'ri-money-dollar-circle-line',
                title: 'Above Scrap Rates',
                description: 'We pay significantly more than scrap dealers by recognizing the true value of your equipment and finding the right buyers.'
              },
              {
                icon: 'ri-file-text-line',
                title: 'Complete Documentation',
                description: 'Full paperwork including certificates of destruction, removal documentation, and payment records for your compliance needs.'
              },
              {
                icon: 'ri-calendar-schedule-line',
                title: 'Turnaround Coordination',
                description: 'We work with your maintenance schedule and turnaround timeline to minimize disruption to your operations.'
              },
              {
                icon: 'ri-shield-check-line',
                title: 'Fully Insured',
                description: 'Complete insurance coverage including general liability and workers compensation for all removal activities.'
              },
              {
                icon: 'ri-team-line',
                title: 'Experienced Team',
                description: '25+ years of experience in industrial equipment with certified rigging crews and safety professionals.'
              },
              {
                icon: 'ri-time-line',
                title: '24-Hour Response',
                description: 'Fast response times and flexible scheduling to meet your project deadlines and operational requirements.'
              }
            ].map((feature, index) => (
              <div key={index} className="bg-white rounded-xl sm:rounded-2xl lg:rounded-3xl p-4 sm:p-6 lg:p-8 text-center hover:shadow-lg transition-all duration-300">
                <div className="w-12 sm:w-16 lg:w-20 h-12 sm:h-16 lg:h-20 bg-[#1B3A5F] rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 lg:mb-6">
                  <i className={`${feature.icon} text-white text-xl sm:text-2xl lg:text-3xl`}></i>
                </div>
                <h3 className="text-[#1B3A5F] text-base sm:text-lg lg:text-xl font-bold mb-2 sm:mb-3 lg:mb-4">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed text-sm sm:text-base">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12 lg:mb-16">
            <h2 className="text-[#1B3A5F] text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4">FREQUENTLY ASKED QUESTIONS</h2>
            <p className="text-gray-600 text-base sm:text-lg lg:text-xl">Common questions about our equipment purchasing process</p>
          </div>
          
          <div className="space-y-4 sm:space-y-6 lg:space-y-8">
            {[
              {
                question: 'How quickly can you provide a quote?',
                answer: 'We typically respond within 24 hours with initial pricing. For detailed quotes, allow 1-3 business days depending on the complexity and size of your inventory.'
              },
              {
                question: 'Do you handle all the removal and logistics?',
                answer: 'Yes, we manage the entire removal process including permits, safety documentation, certified rigging crews, and transportation. You don\'t need to coordinate anything.'
              },
              {
                question: 'What condition does equipment need to be in?',
                answer: 'We purchase equipment in various conditions from excellent to poor. Even damaged or obsolete equipment has value as we can refurbish or sell for parts.'
              },
              {
                question: 'How do you determine pricing?',
                answer: 'Our pricing is based on current market conditions, equipment condition, brand reputation, and demand. We pay significantly more than scrap value by finding the right end users.'
              },
              {
                question: 'Can you work during our turnaround schedule?',
                answer: 'Absolutely. We coordinate closely with your maintenance team and can work within tight turnaround windows. We\'re available 24/7 during critical periods.'
              },
              {
                question: 'What documentation do you provide?',
                answer: 'Complete documentation package including certificates of destruction (if required), removal manifests, payment records, and insurance certificates for your files.'
              }
            ].map((faq, index) => (
              <div key={index} className="bg-[#F5F7FA] rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8">
                <h3 className="text-[#1B3A5F] text-base sm:text-lg lg:text-xl font-bold mb-2 sm:mb-3 lg:mb-4">{faq.question}</h3>
                <p className="text-gray-600 leading-relaxed text-sm sm:text-base">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-[#1B3A5F]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-white text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4 lg:mb-6">READY TO GET STARTED?</h2>
          <p className="text-gray-200 text-base sm:text-lg lg:text-xl mb-6 sm:mb-8 lg:mb-10">
            Contact us today to begin the process. We'll have a quote ready within 24 hours.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center">
            <Link
              to="/contact"
              className="bg-white text-[#1B3A5F] px-6 sm:px-8 lg:px-10 py-3 sm:py-4 rounded-full text-base sm:text-lg lg:text-xl font-semibold hover:bg-gray-100 transition-all duration-300 whitespace-nowrap"
            >
              START THE PROCESS
            </Link>
            <a
              href="tel:(214) 854-5638"
              className="border-2 border-white text-white px-6 sm:px-8 lg:px-10 py-3 sm:py-4 rounded-full text-base sm:text-lg lg:text-xl font-semibold hover:bg-white hover:text-[#1B3A5F] transition-all duration-300 flex items-center justify-center whitespace-nowrap"
            >
              <i className="ri-phone-line mr-2"></i>
              CALL (214) 854-5638
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HowItWorks;
